import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  AddRounded,
  DeleteOutlineRounded,
  DriveFileMoveRounded,
  ToggleOnRounded,
  ToggleOffOutlined,
  AssignmentRounded,
  PlayArrowRounded,
  EditCalendarRounded,
  LockClockRounded,
  CheckRounded,
  CloseRounded,
  NotesRounded,
  EditRounded,
  ForumRounded,
} from '@mui/icons-material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { collection, query, where, doc, getDoc, getDocs } from 'firebase/firestore';
import { FormControlLabel, Switch } from '@mui/material';
import { capApi } from '../../lib/api/cap.api';
import { db } from '../../lib/firebase';
import {
  useTenantStore,
  useTenantTimeZone,
  useTenantDateFormat,
  useTenantTimeFormat,
} from '../../stores/tenant.store';
import { formatInstant, instantToZonedWall } from '@dosfilos/shared';
import {
  assignmentApi,
  type AssignmentSummaryDto,
  type AssignmentType,
  type SubmissionType,
} from '../../lib/api/assignment.api';
import { SectionPicker } from './components/SectionPicker';
import { fetchSectionsOverview } from './components/section-picker.data';
import { TenantDateTimeField } from '../../components/TenantDateTimeField';
import { lmsApi } from '../../lib/api/lms.api';
import { courseTemplateApi } from '../../lib/api/course-template.api';
import { evaluationStandardApi } from '../../lib/api/evaluation-standard.api';
import { errorText } from '../../lib/error-text';
import { eligiblePloOptions, type PloOption } from '../../lib/signature-plo-options';

/** Mirrors `requiresSubmissionChannel` in the domain: these types must expose at
 *  least one submission channel, otherwise the student's submit dialog renders
 *  no inputs at all. `attendance`/`quiz`/`socratic` are legitimately empty. */
function requiresSubmissionChannel(type: AssignmentType): boolean {
  return type === 'reading-review' || type === 'essay' || type === 'project' || type === 'other';
}

/** Flatten an assignment's instructions (plain string or TipTap JSON) to text. */
function instructionsToText(desc?: Record<string, unknown> | string): string {
  if (!desc) return '';
  if (typeof desc === 'string') return desc;
  const collect = (node: unknown): string => {
    if (!node || typeof node !== 'object') return '';
    const n = node as Record<string, unknown>;
    let s = typeof n['text'] === 'string' ? (n['text'] as string) : '';
    if (Array.isArray(n['content'])) s += (n['content'] as unknown[]).map(collect).join('');
    return s;
  };
  const content = (desc as { content?: unknown[] }).content;
  return (Array.isArray(content) ? content.map(collect) : [collect(desc)]).join('\n').trim();
}

/** Convert an ISO UTC instant to the `yyyy-MM-ddTHH:mm` wall-clock string in the
 *  TENANT's zone (not the browser's), the format `<input type="datetime-local">`
 *  expects — so editing prefills the seminary's local time. */
function isoToLocalInput(iso: string, tz: string): string {
  return instantToZonedWall(iso, tz);
}

const TYPE_LABEL: Record<AssignmentType, string> = {
  'reading-review': 'Reseña',
  essay: 'Ensayo',
  quiz: 'Quiz',
  project: 'Proyecto',
  attendance: 'Asistencia',
  socratic: 'Socrático',
  other: 'Otro',
};

/** Los PLOs que una tarea de esta sección puede evaluar: los del programa,
 *  filtrados por el CAP que el programa persigue. La regla vive en
 *  `eligiblePloOptions`; acá sólo se leen los datos. Lo usan los dos diálogos
 *  —crear y editar—, que antes tenían el mismo cargador duplicado y por eso
 *  podían divergir. */
async function loadSignaturePlos(tenantId: string, programId: string): Promise<PloOption[]> {
  const [programSnap, outcomesSnap] = await Promise.all([
    getDoc(doc(db, `tenants/${tenantId}/programs/${programId}`)),
    getDocs(
      query(
        collection(db, `tenants/${tenantId}/programOutcomes`),
        where('programId', '==', programId),
      ),
    ),
  ]);
  const target = (programSnap.data()?.['capTarget'] as { capCode?: string } | undefined)?.capCode;
  const options: PloOption[] = outcomesSnap.docs.map((d) => {
    const data = d.data();
    const es = (data['undergraduate'] as { es?: string } | undefined)?.es ?? '';
    return {
      id: d.id,
      capCode: (data['capCode'] as string) ?? '',
      order: (data['order'] as number) ?? 0,
      label: `${data['sourceOutcomeId'] as string} — ${es}`,
    };
  });
  return eligiblePloOptions(options, target);
}

export function AssignmentsAdminPage({
  embeddedSectionId,
  moduleOptions,
  placementsByAssignment,
  onStructureChanged,
}: {
  embeddedSectionId?: string;
  /** When embedded in the /curso cockpit, the section's modules so a new
   *  assignment can be placed into one on creation. */
  moduleOptions?: { id: string; title: string; sortOrder?: number }[];
  /** Current module placements per assignment id, so the edit dialog can manage
   *  them. */
  placementsByAssignment?: Record<string, { moduleId: string; itemId: string }[]>;
  /** Called after placements change so the cockpit refreshes its structure. */
  onStructureChanged?: () => Promise<void>;
} = {}): JSX.Element {
  const { t } = useTranslation('sis');
  const { t: tCapRow } = useTranslation('cap');
  const { currentTenantId } = useTenantStore();

  // Embedded mode (inside the /curso cockpit tab): the cockpit owns the section
  // picker + page chrome; this component just renders the assignments pane for
  // the given section.
  const embedded = embeddedSectionId !== undefined;

  const [selectedSectionIdState, setSelectedSectionId] = useState('');
  const selectedSectionId = embedded ? (embeddedSectionId ?? '') : selectedSectionIdState;
  const [assignments, setAssignments] = useState<AssignmentSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<AssignmentSummaryDto | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const [editingDueAtId, setEditingDueAtId] = useState<string | null>(null);
  const [dueAtDraft, setDueAtDraft] = useState('');
  const [savingDueAt, setSavingDueAt] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editingLockAtId, setEditingLockAtId] = useState<string | null>(null);
  const [lockAtDraft, setLockAtDraft] = useState('');
  const [savingLockAt, setSavingLockAt] = useState(false);
  // Sección visible AHORA, para que una relectura en vuelo sepa si llegó tarde.
  const sectionIdRef = useRef(selectedSectionId);
  sectionIdRef.current = selectedSectionId;
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [editingDescId, setEditingDescId] = useState<string | null>(null);
  const [descDraft, setDescDraft] = useState('');
  const [savingDesc, setSavingDesc] = useState(false);
  const [descError, setDescError] = useState<string | null>(null);

  // ── By-module view (cockpit only) ─────────────────────────────────────────
  // Groups the list by module placement (LmsModuleItem), ordered by each
  // module's first class date (falling back to sortOrder), so the teacher sees
  // at a glance which task belongs to which class block.
  const canGroupByModule = embedded && !!moduleOptions;
  const [viewMode, setViewMode] = useState<'byModule' | 'flat'>(
    canGroupByModule ? 'byModule' : 'flat',
  );
  const groupedView = canGroupByModule && viewMode === 'byModule';
  const [moduleMinDates, setModuleMinDates] = useState<Record<string, string>>({});
  const [moveMenu, setMoveMenu] = useState<{
    anchor: HTMLElement;
    assignment: AssignmentSummaryDto;
  } | null>(null);

  useEffect(() => {
    if (!canGroupByModule || !currentTenantId || !selectedSectionId) return;
    let cancelled = false;
    void (async () => {
      try {
        const { groups } = await courseTemplateApi.listSectionSessions({
          tenantId: currentTenantId,
          sectionId: selectedSectionId,
        });
        if (cancelled) return;
        const min: Record<string, string> = {};
        for (const g of groups) {
          const prev = min[g.moduleId];
          if (!prev || g.date < prev) min[g.moduleId] = g.date;
        }
        setModuleMinDates(min);
      } catch {
        // Ordering falls back to sortOrder; the view still renders.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canGroupByModule, currentTenantId, selectedSectionId]);

  const assignmentGroups = useMemo(() => {
    if (!groupedView || !moduleOptions) return [];
    const byModule = new Map<string, AssignmentSummaryDto[]>();
    const unplaced: AssignmentSummaryDto[] = [];
    for (const a of assignments) {
      const placements = placementsByAssignment?.[a.id] ?? [];
      if (placements.length === 0) {
        unplaced.push(a);
        continue;
      }
      for (const p of placements) {
        const arr = byModule.get(p.moduleId) ?? [];
        arr.push(a);
        byModule.set(p.moduleId, arr);
      }
    }
    const mods = moduleOptions
      .filter((m) => byModule.has(m.id))
      .sort((x, y) => {
        const dx = moduleMinDates[x.id];
        const dy = moduleMinDates[y.id];
        if (dx && dy && dx !== dy) return dx < dy ? -1 : 1;
        if (dx && !dy) return -1;
        if (!dx && dy) return 1;
        return (x.sortOrder ?? 0) - (y.sortOrder ?? 0);
      });
    const groups: { id: string | null; title: string; items: AssignmentSummaryDto[] }[] = mods.map(
      (m) => ({ id: m.id, title: m.title, items: byModule.get(m.id)! }),
    );
    if (unplaced.length > 0) groups.push({ id: null, title: '', items: unplaced });
    return groups;
  }, [groupedView, moduleOptions, assignments, placementsByAssignment, moduleMinDates]);

  const handleMoveTo = async (a: AssignmentSummaryDto, targetModuleId: string | null) => {
    if (!currentTenantId) return;
    const placements = placementsByAssignment?.[a.id] ?? [];
    try {
      if (targetModuleId === null) {
        if (placements.length === 1) {
          await lmsApi.deleteModuleItem({
            tenantId: currentTenantId,
            itemId: placements[0]!.itemId,
          });
        }
      } else if (placements.length === 0) {
        await lmsApi.createModuleItem({
          tenantId: currentTenantId,
          sectionId: a.sectionId,
          moduleId: targetModuleId,
          type: 'assignment',
          targetId: a.id,
        });
      } else if (placements.length === 1) {
        await lmsApi.updateModuleItem({
          tenantId: currentTenantId,
          itemId: placements[0]!.itemId,
          moduleId: targetModuleId,
        });
      }
      await onStructureChanged?.();
    } catch (e) {
      setError(errorText(e));
    }
  };

  // Dates render in the tenant's zone with a fixed dd-mm-yyyy format (a date
  // format is a regional convention, not a linguistic one — decoupled from i18n).
  const tenantTz = useTenantTimeZone();
  const tenantFmt = useTenantDateFormat();
  const tenantTimeFmt = useTenantTimeFormat();

  /** Vuelve a leer la lista SIN spinner y SIN mover las filas de lugar.
   *
   *  EL PROBLEMA QUE RESUELVE. Cada edición en línea terminaba en `reload()`,
   *  que pone `loading` en true y cambia la lista entera por un spinner:
   *  corregir ocho fechas seguidas se sentía como ocho recargas de página, con
   *  el scroll saltando cada vez.
   *
   *  POR QUÉ SE RELEE EN VEZ DE PARCHEAR EN MEMORIA: lo que se edita es una
   *  hora LOCAL («2026-10-05T23:59») y lo que la fila muestra es un instante
   *  ISO que convierte el servidor en la zona del centro. Escribir el borrador
   *  local directamente en el estado pintaría una fecha distinta de la
   *  guardada hasta la próxima carga — el peor error posible en una pantalla
   *  de fechas. El callable sólo devuelve `{ success }`, así que la fuente de
   *  la verdad sigue siendo el servidor.
   *
   *  Y SE CONSERVA EL ORDEN DE PANTALLA: la lista se ordena por fecha de
   *  entrega al cargarla, así que al cambiar una fecha la fila saltaría de
   *  lugar justo mientras se la edita. Ese orden es del momento de carga, no un
   *  invariante vivo; al volver a entrar a la sección queda ordenado. */
  const quietRefresh = async () => {
    if (!currentTenantId || !selectedSectionId) return;
    const forSection = selectedSectionId;
    try {
      const { items } = await assignmentApi.list({
        tenantId: currentTenantId,
        sectionId: forSection,
      });
      // Si mientras tanto se cambió de sección, estas tareas son de la otra:
      // mezclarlas dejaría dos secciones en la misma lista. A diferencia de
      // `reload`, que reemplaza todo, acá se fusiona — así que hay que mirar.
      if (sectionIdRef.current !== forSection) return;
      setAssignments((prev) => {
        const byId = new Map(items.map((i) => [i.id, i]));
        const kept = prev.map((a) => byId.get(a.id) ?? a).filter((a) => byId.has(a.id));
        const seen = new Set(kept.map((a) => a.id));
        return [...kept, ...items.filter((i) => !seen.has(i.id))];
      });
    } catch {
      /* Silencioso a propósito: el cambio YA se guardó y el error de una
         relectura no debe alarmar sobre una edición que salió bien. */
    }
  };

  const reload = async () => {
    if (!currentTenantId || !selectedSectionId) {
      setAssignments([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { items } = await assignmentApi.list({
        tenantId: currentTenantId,
        sectionId: selectedSectionId,
      });
      // Order: by due date ascending (assignments without a due date go last),
      // then alphabetically by name.
      const sorted = [...items].sort((a, b) => {
        if (a.dueAt && b.dueAt && a.dueAt !== b.dueAt) return a.dueAt < b.dueAt ? -1 : 1;
        if (!a.dueAt && b.dueAt) return 1;
        if (a.dueAt && !b.dueAt) return -1;
        return a.name.localeCompare(b.name, 'es', { numeric: true });
      });
      setAssignments(sorted);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId, selectedSectionId]);

  const handlePublishToggle = async (a: AssignmentSummaryDto) => {
    if (!currentTenantId) return;
    try {
      await assignmentApi.publish({
        tenantId: currentTenantId,
        assignmentId: a.id,
        publish: !a.publishedAt,
      });
      await reload();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const handleDelete = async () => {
    if (!currentTenantId || !deletingId) return;
    try {
      await assignmentApi.delete({ tenantId: currentTenantId, assignmentId: deletingId });
      setDeletingId(null);
      await reload();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const handleStartEditDueAt = (a: AssignmentSummaryDto) => {
    setEditingDueAtId(a.id);
    setDueAtDraft(a.dueAt ? isoToLocalInput(a.dueAt, tenantTz) : '');
    setEditError(null);
  };

  const handleCancelEditDueAt = () => {
    setEditingDueAtId(null);
    setDueAtDraft('');
    setEditError(null);
  };

  /** Guarda Entrega y/o Cierre — LAS DOS EN UNA SOLA ESCRITURA si los dos
   *  campos están abiertos en esa fila.
   *
   *  POR QUÉ NO SE PUEDE GUARDAR DE A UNA. El servidor valida `Cierre >=
   *  Entrega` sobre el par EFECTIVO: el valor que llega, y si no llega, el que
   *  ya estaba guardado. Mover las dos fechas de a una choca siempre contra el
   *  valor viejo de la otra — correr un par de octubre a noviembre se rechaza
   *  en el primer guardado.
   *
   *  ANTES la contraparte vivía en un estado que se fijaba AL ABRIR el editor,
   *  y encima era UNA sola variable para los dos campos: abrir el segundo
   *  pisaba la del primero, y ninguno veía lo que se estaba escribiendo en el
   *  otro. Se sentía como «no me deja cambiarla hasta que cierro el campo y lo
   *  vuelvo a abrir». Ahora la contraparte se resuelve AL GUARDAR, tomando el
   *  borrador del otro campo si está abierto y el valor guardado si no.
   *
   *  `origin` sólo elige de qué campo es el mensaje de error; lo que se manda
   *  no depende de en cuál se hizo clic. */
  const handleSaveDates = async (origin: 'due' | 'lock') => {
    const id = origin === 'due' ? editingDueAtId : editingLockAtId;
    if (!currentTenantId || !id || savingDueAt || savingLockAt) return;
    const dueOpen = editingDueAtId === id;
    const lockOpen = editingLockAtId === id;

    if (dueOpen && !dueAtDraft) {
      setEditError(t('assignmentsAdmin.editDueDate.missingValue'));
      return;
    }
    if (lockOpen && !lockAtDraft) {
      setEditError(t('assignmentsAdmin.editLockDate.missingValue'));
      return;
    }

    // El par que va a quedar: el borrador donde hay editor abierto, lo guardado
    // donde no. Mismo formato ('yyyy-MM-ddTHH:mm'), así que comparan cronológico.
    const row = assignments.find((a) => a.id === id);
    // La tarea dejó de estar en la lista (la borraron desde otra pestaña, o una
    // relectura la sacó). Mandar el update daría un `not found` cuyo error no se
    // vería: la fila ya no se renderiza, así que el editor abierto es invisible.
    // Cerrar es lo único honesto.
    if (!row) {
      handleCancelEditDueAt();
      handleCancelEditLockAt();
      return;
    }
    const storedDue = row.dueAt ? isoToLocalInput(row.dueAt, tenantTz) : '';
    const storedLock = row.lockAt ? isoToLocalInput(row.lockAt, tenantTz) : '';
    const due = dueOpen ? dueAtDraft : storedDue;
    const lock = lockOpen ? lockAtDraft : storedLock;
    if (due && lock && due > lock) {
      setEditError(
        t(
          origin === 'due'
            ? 'assignmentsAdmin.editDueDate.afterLock'
            : 'assignmentsAdmin.editLockDate.beforeDue',
        ),
      );
      return;
    }

    // SÓLO LO QUE CAMBIÓ. Reenviar la Entrega igual a la guardada no sería
    // inofensivo: el servidor marca `dueAtOverridden` en toda edición manual de
    // la entrega, lo que fija la fecha frente a una regeneración del syllabus.
    // Con los dos campos abiertos, guardar el Cierre no debe fijar la Entrega.
    // Se manda la hora local de pared tal cual; el servidor la convierte en la
    // zona del centro.
    const updates: Record<string, string> = {};
    if (dueOpen && dueAtDraft !== storedDue) updates['dueAt'] = dueAtDraft;
    if (lockOpen && lockAtDraft !== storedLock) updates['lockAt'] = lockAtDraft;

    // Se abrieron los campos y no se tocó nada: cerrar sin gastar una escritura.
    if (Object.keys(updates).length === 0) {
      handleCancelEditDueAt();
      handleCancelEditLockAt();
      return;
    }

    setSavingDueAt(dueOpen);
    setSavingLockAt(lockOpen);
    setEditError(null);
    try {
      await assignmentApi.update({ tenantId: currentTenantId, assignmentId: id, updates });
      if (dueOpen) {
        setEditingDueAtId(null);
        setDueAtDraft('');
      }
      if (lockOpen) {
        setEditingLockAtId(null);
        setLockAtDraft('');
      }
      await quietRefresh();
    } catch (e) {
      setEditError(
        errorText(
          e,
          t(
            origin === 'due'
              ? 'assignmentsAdmin.editDueDate.updateError'
              : 'assignmentsAdmin.editLockDate.updateError',
          ),
        ),
      );
    } finally {
      setSavingDueAt(false);
      setSavingLockAt(false);
    }
  };

  const handleSaveDueAt = () => handleSaveDates('due');

  const handleStartEditLockAt = (a: AssignmentSummaryDto) => {
    setEditingLockAtId(a.id);
    setLockAtDraft(a.lockAt ? isoToLocalInput(a.lockAt, tenantTz) : '');
    setEditError(null);
  };

  const handleCancelEditLockAt = () => {
    setEditingLockAtId(null);
    setLockAtDraft('');
    setEditError(null);
  };

  const handleSaveLockAt = () => handleSaveDates('lock');

  const handleStartEditName = (a: AssignmentSummaryDto) => {
    setEditingNameId(a.id);
    setNameDraft(a.name);
    setNameError(null);
  };

  const handleCancelEditName = () => {
    setEditingNameId(null);
    setNameDraft('');
    setNameError(null);
  };

  const handleSaveName = async () => {
    // Enter guarda, y Enter repetido llegaría antes de que el input quede
    // deshabilitado: sin esto, dos escrituras por el mismo título.
    if (!currentTenantId || !editingNameId || savingName) return;
    const name = nameDraft.trim();
    if (!name) {
      setNameError(t('assignmentsAdmin.editName.missingValue'));
      return;
    }
    // Sin cambios: cerrar y no gastar una escritura ni una relectura. Corregir
    // ocho títulos seguidos implica abrir varios sin tocarlos.
    if (name === assignments.find((a) => a.id === editingNameId)?.name) {
      handleCancelEditName();
      return;
    }
    setSavingName(true);
    setNameError(null);
    try {
      await assignmentApi.update({
        tenantId: currentTenantId,
        assignmentId: editingNameId,
        updates: { name },
      });
      setEditingNameId(null);
      setNameDraft('');
      await quietRefresh();
    } catch (e) {
      setNameError(errorText(e, t('assignmentsAdmin.editName.updateError')));
    } finally {
      setSavingName(false);
    }
  };

  const handleStartEditDesc = (a: AssignmentSummaryDto) => {
    setEditingDescId(a.id);
    setDescDraft(instructionsToText(a.description));
    setDescError(null);
  };

  const handleSaveDesc = async () => {
    if (!currentTenantId || !editingDescId) return;
    setSavingDesc(true);
    setDescError(null);
    try {
      await assignmentApi.update({
        tenantId: currentTenantId,
        assignmentId: editingDescId,
        updates: { description: descDraft },
      });
      setEditingDescId(null);
      setDescDraft('');
      await quietRefresh();
    } catch (e) {
      setDescError(errorText(e, t('assignmentsAdmin.editInstructions.updateError')));
    } finally {
      setSavingDesc(false);
    }
  };

  // Inscritos de LA sección elegida: un conteo. Antes esta pantalla cargaba
  // todas las secciones del seminario (con sus cursos, períodos, docentes e
  // inscritos) solo para sacar este número.
  const enrolledQ = useQuery({
    queryKey: ['section-enrolled', currentTenantId, selectedSectionId],
    enabled: !!currentTenantId && !!selectedSectionId,
    // Del servidor (PR 3A): el conteo ya no se hace leyendo inscripciones.
    queryFn: async () =>
      (await fetchSectionsOverview(currentTenantId!, [selectedSectionId])).enrolled.get(
        selectedSectionId,
      ) ?? 0,
    staleTime: 60_000,
  });
  const totalEnrolled = enrolledQ.data ?? 0;

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
      {!embedded && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {t('assignmentsAdmin.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('assignmentsAdmin.subtitle')}
            </Typography>
          </Box>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {embedded ? (
        <Stack
          direction="row"
          justifyContent={canGroupByModule ? 'space-between' : 'flex-end'}
          alignItems="center"
          sx={{ mb: 2 }}
        >
          {canGroupByModule && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(_, v: 'byModule' | 'flat' | null) => {
                if (v) setViewMode(v);
              }}
            >
              <ToggleButton value="byModule" sx={{ textTransform: 'none', px: 1.5 }}>
                {t('assignmentsAdmin.view.byModule')}
              </ToggleButton>
              <ToggleButton value="flat" sx={{ textTransform: 'none', px: 1.5 }}>
                {t('assignmentsAdmin.view.flat')}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            disabled={!selectedSectionId}
            onClick={() => setNewOpen(true)}
          >
            {t('assignmentsAdmin.new')}
          </Button>
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <SectionPicker value={selectedSectionId} onChange={setSelectedSectionId} />
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              disabled={!selectedSectionId}
              onClick={() => setNewOpen(true)}
            >
              {t('assignmentsAdmin.new')}
            </Button>
          </Stack>
        </Paper>
      )}

      {!selectedSectionId && !embedded && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">
            {t('assignmentsAdmin.selectSectionPrompt')}
          </Typography>
        </Paper>
      )}

      {selectedSectionId && loading && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">{t('assignmentsAdmin.loading')}</Typography>
        </Stack>
      )}

      {selectedSectionId && !loading && assignments.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <AssignmentRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">{t('assignmentsAdmin.empty')}</Typography>
        </Paper>
      )}

      {selectedSectionId &&
        !loading &&
        assignments.length > 0 &&
        (() => {
          // Row renderer shared by the flat list and the by-module view. Kept as
          // a closure so the inline-edit state/handlers above stay in scope.
          const renderAssignmentRow = (a: AssignmentSummaryDto): JSX.Element => (
            <Paper
              key={a.id}
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                  <Chip size="small" label={t(`assignmentsAdmin.types.${a.type}`)} />
                  {editingNameId === a.id ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1 }}>
                      <TextField
                        size="small"
                        autoFocus
                        fullWidth
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        disabled={savingName}
                        error={!!nameError}
                        helperText={nameError || undefined}
                        inputProps={{ 'aria-label': t('assignmentsAdmin.editName.label') }}
                        /* Enter guarda y Escape descarta: corregir varios
                           títulos seguidos no debería exigir apuntar al ícono
                           cada vez. */
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void handleSaveName();
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            handleCancelEditName();
                          }
                        }}
                      />
                      <Tooltip title={t('assignmentsAdmin.editName.save')}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleSaveName}
                            disabled={savingName || !nameDraft.trim()}
                          >
                            {savingName ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckRounded fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('assignmentsAdmin.editName.cancel')}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleCancelEditName}
                            disabled={savingName}
                          >
                            <CloseRounded fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {a.name}
                      </Typography>
                      <Tooltip title={t('assignmentsAdmin.editName.tooltip')}>
                        <IconButton size="small" onClick={() => handleStartEditName(a)}>
                          <EditRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {!a.publishedAt && (
                    <Chip
                      size="small"
                      label={t('assignmentsAdmin.row.draft')}
                      color="warning"
                      variant="outlined"
                    />
                  )}
                  {a.isSignatureAssignment && (
                    <Chip
                      size="small"
                      color="secondary"
                      variant="outlined"
                      label={tCapRow('signature.chip', {
                        plo: a.evaluatesOutcomeId?.split('_').pop() ?? '',
                      })}
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
                  {/* Quiz has no due date and its close is edited in the quiz builder. */}
                  {a.type !== 'quiz' &&
                    (editingDueAtId === a.id ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TenantDateTimeField
                          label={t('assignmentsAdmin.editDueDate.label')}
                          value={dueAtDraft}
                          onChange={setDueAtDraft}
                          disabled={savingDueAt}
                        />
                        <Tooltip title={t('assignmentsAdmin.editDueDate.save')}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleSaveDueAt}
                              disabled={savingDueAt || !dueAtDraft}
                            >
                              {savingDueAt ? (
                                <CircularProgress size={16} />
                              ) : (
                                <CheckRounded fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('assignmentsAdmin.editDueDate.cancel')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={handleCancelEditDueAt}
                              disabled={savingDueAt}
                            >
                              <CloseRounded fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {t('assignmentsAdmin.row.due')}:{' '}
                          {a.dueAt
                            ? formatInstant(a.dueAt, tenantTz, tenantFmt, tenantTimeFmt)
                            : t('assignmentsAdmin.row.noDueDate')}
                        </Typography>
                        <Tooltip title={t('assignmentsAdmin.editDueDate.tooltip')}>
                          <IconButton size="small" onClick={() => handleStartEditDueAt(a)}>
                            <EditCalendarRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                  {editingLockAtId === a.id ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TenantDateTimeField
                        label={t('assignmentsAdmin.editLockDate.label')}
                        value={lockAtDraft}
                        onChange={setLockAtDraft}
                        disabled={savingLockAt}
                      />
                      <Tooltip title={t('assignmentsAdmin.editLockDate.save')}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleSaveLockAt}
                            disabled={savingLockAt || !lockAtDraft}
                          >
                            {savingLockAt ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckRounded fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('assignmentsAdmin.editLockDate.cancel')}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleCancelEditLockAt}
                            disabled={savingLockAt}
                          >
                            <CloseRounded fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {t('assignmentsAdmin.row.lock')}:{' '}
                        {a.lockAt
                          ? formatInstant(a.lockAt, tenantTz, tenantFmt, tenantTimeFmt)
                          : t('assignmentsAdmin.row.noLockDate')}
                      </Typography>
                      {a.type !== 'quiz' && (
                        <Tooltip title={t('assignmentsAdmin.editLockDate.tooltip')}>
                          <IconButton size="small" onClick={() => handleStartEditLockAt(a)}>
                            <LockClockRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {t('assignmentsAdmin.row.weightAndPoints', {
                      weight: a.weight,
                      points: a.pointsPossible,
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('assignmentsAdmin.row.submissionsAndGrading', {
                      submissions: a.submissionCount,
                      enrolled: totalEnrolled,
                      graded: a.gradedCount,
                    })}
                  </Typography>
                </Stack>
                {(editingDueAtId === a.id || editingLockAtId === a.id) && editError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {editError}
                  </Typography>
                )}
              </Box>
              <Tooltip title={t('assignmentsAdmin.editInstructions.tooltip')}>
                <IconButton onClick={() => handleStartEditDesc(a)}>
                  <NotesRounded />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  a.type === 'quiz' && !a.publishedAt
                    ? t('assignmentsAdmin.row.publishQuizInBuilder')
                    : a.publishedAt
                      ? t('assignmentsAdmin.row.unpublish')
                      : t('assignmentsAdmin.row.publish')
                }
              >
                <span>
                  <IconButton
                    onClick={() => handlePublishToggle(a)}
                    // A draft quiz must be published from the builder (validates + writes
                    // the definition/key); the raw toggle would publish an empty quiz.
                    disabled={a.type === 'quiz' && !a.publishedAt}
                  >
                    {a.publishedAt ? (
                      <ToggleOnRounded color="primary" />
                    ) : (
                      <ToggleOffOutlined color="disabled" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              {a.type === 'quiz' ? (
                <Tooltip title={t('assignmentsAdmin.row.editQuiz')}>
                  <IconButton
                    onClick={() =>
                      navigate(`/assignments/quiz/${a.id}/edit?sectionId=${a.sectionId}`)
                    }
                  >
                    <EditRounded />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={t('assignmentsAdmin.row.edit')}>
                  <IconButton onClick={() => setEditingAssignment(a)}>
                    <EditRounded />
                  </IconButton>
                </Tooltip>
              )}
              {a.type === 'socratic' ? (
                <Tooltip title={t('assignmentsAdmin.row.reviewSocratic')}>
                  <IconButton color="primary" onClick={() => navigate(`/socratic/${a.id}/review`)}>
                    <ForumRounded />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={t('assignmentsAdmin.row.grade')}>
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() =>
                        navigate(`/assignments/${a.id}/grade?section=${a.sectionId}`, {
                          state: { assignment: a },
                        })
                      }
                      disabled={a.submissionCount === 0}
                    >
                      <PlayArrowRounded />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {groupedView && (
                <Tooltip
                  title={
                    (placementsByAssignment?.[a.id] ?? []).length > 1
                      ? t('assignmentsAdmin.row.managePlacements')
                      : t('assignmentsAdmin.row.moveToModule')
                  }
                >
                  <IconButton
                    onClick={(e) => {
                      // Multi-placed → the edit dialog's multi-select is the
                      // right surface; single/none → quick move menu.
                      if ((placementsByAssignment?.[a.id] ?? []).length > 1) {
                        setEditingAssignment(a);
                      } else {
                        setMoveMenu({ anchor: e.currentTarget, assignment: a });
                      }
                    }}
                  >
                    <DriveFileMoveRounded />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('assignmentsAdmin.row.delete')}>
                <IconButton color="error" onClick={() => setDeletingId(a.id)}>
                  <DeleteOutlineRounded />
                </IconButton>
              </Tooltip>
            </Paper>
          );

          if (!groupedView) {
            return <Stack spacing={1}>{assignments.map(renderAssignmentRow)}</Stack>;
          }
          return (
            <Stack spacing={2}>
              {assignmentGroups.map((g) => (
                <Box key={g.id ?? '__none__'}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {g.id ? g.title : t('assignmentsAdmin.group.noModule')}
                    </Typography>
                    <Chip size="small" label={g.items.length} sx={{ height: 20 }} />
                  </Stack>
                  <Stack spacing={1}>{g.items.map(renderAssignmentRow)}</Stack>
                </Box>
              ))}
            </Stack>
          );
        })()}

      <Menu anchorEl={moveMenu?.anchor ?? null} open={!!moveMenu} onClose={() => setMoveMenu(null)}>
        {(moduleOptions ?? [])
          .filter(
            (m) =>
              !(placementsByAssignment?.[moveMenu?.assignment.id ?? ''] ?? []).some(
                (p) => p.moduleId === m.id,
              ),
          )
          .map((m) => (
            <MenuItem
              key={m.id}
              onClick={() => {
                const a = moveMenu?.assignment;
                setMoveMenu(null);
                if (a) void handleMoveTo(a, m.id);
              }}
            >
              {m.title}
            </MenuItem>
          ))}
        {(placementsByAssignment?.[moveMenu?.assignment.id ?? ''] ?? []).length === 1 && (
          <MenuItem
            onClick={() => {
              const a = moveMenu?.assignment;
              setMoveMenu(null);
              if (a) void handleMoveTo(a, null);
            }}
            sx={{ color: 'error.main' }}
          >
            {t('assignmentsAdmin.row.removeFromModule')}
          </MenuItem>
        )}
      </Menu>

      <NewAssignmentDialog
        open={newOpen}
        sectionId={selectedSectionId}
        {...(moduleOptions ? { moduleOptions } : {})}
        onClose={() => setNewOpen(false)}
        onCreated={async () => {
          setNewOpen(false);
          await reload();
        }}
      />

      <EditAssignmentDialog
        assignment={editingAssignment}
        tenantTz={tenantTz}
        {...(moduleOptions ? { moduleOptions } : {})}
        {...(placementsByAssignment ? { placementsByAssignment } : {})}
        {...(onStructureChanged ? { onStructureChanged } : {})}
        onClose={() => setEditingAssignment(null)}
        onSaved={async () => {
          setEditingAssignment(null);
          await reload();
        }}
      />

      <Dialog open={!!editingDescId} onClose={() => setEditingDescId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('assignmentsAdmin.editInstructions.title')}</DialogTitle>
        <DialogContent>
          {descError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {descError}
            </Alert>
          )}
          <TextField
            autoFocus
            label={t('assignmentsAdmin.editInstructions.label')}
            multiline
            minRows={8}
            fullWidth
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingDescId(null)}>
            {t('assignmentsAdmin.editInstructions.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSaveDesc} disabled={savingDesc}>
            {savingDesc
              ? t('assignmentsAdmin.editInstructions.saving')
              : t('assignmentsAdmin.editInstructions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deletingId} onClose={() => setDeletingId(null)}>
        <DialogTitle>{t('assignmentsAdmin.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('assignmentsAdmin.deleteDialog.body')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingId(null)}>
            {t('assignmentsAdmin.deleteDialog.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            {t('assignmentsAdmin.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function NewAssignmentDialog({
  open,
  sectionId,
  moduleOptions,
  onClose,
  onCreated,
}: {
  open: boolean;
  sectionId: string;
  /** When present, offer a "Módulo (opcional)" select; a chosen module places
   *  the new assignment inside it as an item on creation. */
  moduleOptions?: { id: string; title: string }[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const { t: tCap } = useTranslation('cap');
  const [type, setType] = useState<AssignmentType>('reading-review');
  const [moduleId, setModuleId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [lockAt, setLockAt] = useState('');
  const [weight, setWeight] = useState(0);
  const [points, setPoints] = useState(100);
  const [submissionTypes, setSubmissionTypes] = useState<SubmissionType[]>(['file']);
  const [publish, setPublish] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // CAP signature (evaluates a PLO → the 'E' in the alignment matrix). Parity with
  // the edit dialog so it can be marked at creation, not only by re-opening to edit.
  const [isSignature, setIsSignature] = useState(false);
  const [evaluatesOutcomeId, setEvaluatesOutcomeId] = useState('');
  const [ploOptions, setPloOptions] = useState<
    { id: string; label: string; capCode: string; order: number }[]
  >([]);
  useEffect(() => {
    if (!open || !currentTenantId || !sectionId) return;
    let cancelled = false;
    void (async () => {
      const sectionSnap = await getDoc(doc(db, `tenants/${currentTenantId}/sections/${sectionId}`));
      const programId = sectionSnap.data()?.['programId'] as string | undefined;
      if (!programId) {
        if (!cancelled) setPloOptions([]);
        return;
      }
      const options = await loadSignaturePlos(currentTenantId, programId);
      if (!cancelled) setPloOptions(options);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, currentTenantId, sectionId]);
  // Rubric from the section's Program catalog (drives the assisted review).
  const [rubricRef, setRubricRef] = useState('');
  const [programRubrics, setProgramRubrics] = useState<{ id: string; title: string }[]>([]);
  // A quiz needs room to build many questions — it lives on its own page, not in
  // this modal. Picking "Quiz" here hands off to the dedicated builder.
  const isQuiz = type === 'quiz';
  // A socratic dialogue is a FORMATIVE assignment: no upload, no points/weight;
  // it carries the dialogue config (root question, pinned standard, level, attempts).
  const isSocratic = type === 'socratic';
  const [socStandards, setSocStandards] = useState<{ id: string; title: string }[]>([]);
  const [socStandardId, setSocStandardId] = useState('');
  const [socRootQuestion, setSocRootQuestion] = useState('');
  const [socLevel, setSocLevel] = useState<'review' | 'probe' | 'defense'>('probe');
  const [socAudience, setSocAudience] = useState<'lay' | 'masters' | 'doctorate'>('lay');
  const [socMaxAttempts, setSocMaxAttempts] = useState(3);

  useEffect(() => {
    if (open) {
      setType('reading-review');
      setName('');
      setDescription('');
      setDueAt('');
      setLockAt('');
      setWeight(0);
      setPoints(100);
      setSubmissionTypes(['file']);
      setPublish(true);
      setErr(null);
      setRubricRef('');
      setModuleId('');
      setSocStandardId('');
      setSocRootQuestion('');
      setSocLevel('probe');
      setSocMaxAttempts(3);
    }
  }, [open]);

  // Load approved evaluation standards for the socratic standard picker.
  useEffect(() => {
    if (!open || !currentTenantId) return;
    let cancelled = false;
    evaluationStandardApi
      .list({ tenantId: currentTenantId })
      .then((r) => {
        if (cancelled) return;
        setSocStandards(
          r.standards
            .filter((s) => s.status === 'approved')
            .map((s) => ({ id: s.id, title: s.title })),
        );
      })
      .catch(() => {
        if (!cancelled) setSocStandards([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, currentTenantId]);

  // Load the section's Program rubric catalog so the assignment can reference one.
  useEffect(() => {
    if (!open || !currentTenantId || !sectionId) return;
    let cancelled = false;
    (async () => {
      try {
        const secSnap = await getDoc(doc(db, `tenants/${currentTenantId}/sections/${sectionId}`));
        const programId = secSnap.exists()
          ? (secSnap.data()?.['programId'] as string | undefined)
          : undefined;
        if (!programId) {
          if (!cancelled) setProgramRubrics([]);
          return;
        }
        const progSnap = await getDoc(doc(db, `tenants/${currentTenantId}/programs/${programId}`));
        const rubrics =
          (progSnap.data()?.['rubrics'] as { id: string; title?: string }[] | undefined) ?? [];
        if (!cancelled)
          setProgramRubrics(rubrics.map((r) => ({ id: r.id, title: r.title || r.id })));
      } catch {
        if (!cancelled) setProgramRubrics([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, currentTenantId, sectionId]);

  // An upload-based assignment with no channel is unsubmittable for the student.
  const missingChannel = requiresSubmissionChannel(type) && submissionTypes.length === 0;

  // Quiz: hand off to the dedicated builder page, carrying whatever the teacher
  // already typed so nothing is lost. No server call happens here.
  const goToQuizBuilder = () => {
    navigate(`/assignments/quiz/new?sectionId=${encodeURIComponent(sectionId)}`, {
      state: { name, description, dueAt, weight },
    });
  };

  const handleSubmit = async () => {
    if (!currentTenantId) return;
    if (!name.trim()) {
      setErr(t('assignmentsAdmin.newDialog.nameRequired'));
      return;
    }
    // Grace window: the close date may not be BEFORE the due date.
    if (dueAt && lockAt && lockAt < dueAt) {
      setErr(t('assignmentsAdmin.editLockDate.beforeDue'));
      return;
    }
    if (missingChannel) {
      setErr(t('assignmentsAdmin.newDialog.submissionTypesRequired'));
      return;
    }
    if (isSocratic && (!socRootQuestion.trim() || !socStandardId)) {
      setErr(t('assignmentsAdmin.socratic.configRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const { id } = await assignmentApi.create({
        tenantId: currentTenantId,
        sectionId,
        type,
        name: name.trim(),
        description: description.trim(),
        // Socratic is formative: the server forces weight/points to 0 and no upload.
        weight: isSocratic ? 0 : weight,
        pointsPossible: isSocratic ? 0 : points,
        submissionTypes: isSocratic ? [] : submissionTypes,
        ...(isSocratic
          ? {
              socraticConfig: {
                rootQuestion: socRootQuestion.trim(),
                standardId: socStandardId,
                level: socLevel,
                maxAttempts: socMaxAttempts,
                audienceLevel: socAudience,
              },
            }
          : {}),
        // Wall-clock as-is; the server converts it in the tenant's zone.
        ...(dueAt ? { dueAt } : {}),
        ...(lockAt ? { lockAt } : {}),
        ...(rubricRef && !isSocratic ? { rubricRef } : {}),
        publish,
      });
      // Optionally place the new assignment inside a module as an item.
      if (moduleId) {
        await lmsApi.createModuleItem({
          tenantId: currentTenantId,
          sectionId,
          moduleId,
          type: 'assignment',
          targetId: id,
        });
      }
      // Tag as a CAP signature assignment → produces the 'E' in the alignment matrix.
      if (isSignature && evaluatesOutcomeId) {
        await capApi.tagSignatureAssignment({
          tenantId: currentTenantId,
          assignmentId: id,
          isSignature: true,
          evaluatesOutcomeId,
        });
      }
      await onCreated();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('assignmentsAdmin.newDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Alert severity="error">{err}</Alert>}
          <Stack direction="row" spacing={1}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('assignmentsAdmin.newDialog.type')}</InputLabel>
              <Select
                label={t('assignmentsAdmin.newDialog.type')}
                value={type}
                onChange={(e) => setType(e.target.value as AssignmentType)}
              >
                {(Object.keys(TYPE_LABEL) as AssignmentType[]).map((tp) => (
                  <MenuItem key={tp} value={tp}>
                    {t(`assignmentsAdmin.types.${tp}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label={t('assignmentsAdmin.newDialog.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField
            size="small"
            label={t('assignmentsAdmin.newDialog.instructions')}
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {moduleOptions && moduleOptions.length > 0 && !isQuiz && (
            <FormControl size="small" fullWidth>
              <InputLabel>
                {t('assignmentsAdmin.newDialog.module', { defaultValue: 'Módulo' })}
              </InputLabel>
              <Select
                label={t('assignmentsAdmin.newDialog.module', { defaultValue: 'Módulo' })}
                value={moduleId}
                onChange={(e) => setModuleId(e.target.value)}
              >
                <MenuItem value="">
                  <em>
                    {t('assignmentsAdmin.newDialog.moduleNone', { defaultValue: 'Sin módulo' })}
                  </em>
                </MenuItem>
                {moduleOptions.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {t('assignmentsAdmin.newDialog.moduleHelp', {
                  defaultValue:
                    'Colócala en un módulo para que el alumno la vea y la complete desde ahí.',
                })}
              </FormHelperText>
            </FormControl>
          )}
          {isSocratic && (
            <>
              <TextField
                size="small"
                label={t('assignmentsAdmin.socratic.rootQuestion')}
                value={socRootQuestion}
                onChange={(e) => setSocRootQuestion(e.target.value)}
                multiline
                rows={2}
                helperText={t('assignmentsAdmin.socratic.rootQuestionHelp')}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>{t('assignmentsAdmin.socratic.standard')}</InputLabel>
                <Select
                  label={t('assignmentsAdmin.socratic.standard')}
                  value={socStandardId}
                  onChange={(e) => setSocStandardId(e.target.value)}
                >
                  {socStandards.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.title}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {socStandards.length === 0
                    ? t('assignmentsAdmin.socratic.noStandards')
                    : t('assignmentsAdmin.socratic.standardHelp')}
                </FormHelperText>
              </FormControl>
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>{t('assignmentsAdmin.socratic.level')}</InputLabel>
                  <Select
                    label={t('assignmentsAdmin.socratic.level')}
                    value={socLevel}
                    onChange={(e) => setSocLevel(e.target.value as 'review' | 'probe' | 'defense')}
                  >
                    <MenuItem value="review">
                      {t('assignmentsAdmin.socratic.levels.review')}
                    </MenuItem>
                    <MenuItem value="probe">{t('assignmentsAdmin.socratic.levels.probe')}</MenuItem>
                    <MenuItem value="defense">
                      {t('assignmentsAdmin.socratic.levels.defense')}
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>{t('assignmentsAdmin.socratic.audience')}</InputLabel>
                  <Select
                    label={t('assignmentsAdmin.socratic.audience')}
                    value={socAudience}
                    onChange={(e) =>
                      setSocAudience(e.target.value as 'lay' | 'masters' | 'doctorate')
                    }
                  >
                    <MenuItem value="lay">{t('assignmentsAdmin.socratic.audiences.lay')}</MenuItem>
                    <MenuItem value="masters">
                      {t('assignmentsAdmin.socratic.audiences.masters')}
                    </MenuItem>
                    <MenuItem value="doctorate">
                      {t('assignmentsAdmin.socratic.audiences.doctorate')}
                    </MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  label={t('assignmentsAdmin.socratic.maxAttempts')}
                  value={socMaxAttempts}
                  onChange={(e) => setSocMaxAttempts(Math.max(1, Number(e.target.value) || 1))}
                  sx={{ width: 140 }}
                  inputProps={{ min: 1, max: 20 }}
                />
              </Stack>
            </>
          )}
          {!isQuiz && !isSocratic && (
            <FormControl size="small" fullWidth>
              <InputLabel>{t('assignmentsAdmin.newDialog.rubric')}</InputLabel>
              <Select
                label={t('assignmentsAdmin.newDialog.rubric')}
                value={rubricRef}
                onChange={(e) => setRubricRef(e.target.value)}
                disabled={programRubrics.length === 0}
              >
                <MenuItem value="">
                  <em>{t('assignmentsAdmin.newDialog.rubricNone')}</em>
                </MenuItem>
                {programRubrics.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {programRubrics.length === 0
                  ? t('assignmentsAdmin.newDialog.rubricEmpty')
                  : t('assignmentsAdmin.newDialog.rubricHelp')}
              </FormHelperText>
            </FormControl>
          )}
          {/* Quiz sets its due/weight/points on the dedicated builder page. */}
          {!isQuiz && (
            <Stack spacing={2}>
              <Stack spacing={2}>
                <TenantDateTimeField
                  label={t('assignmentsAdmin.newDialog.dueAt')}
                  value={dueAt}
                  onChange={setDueAt}
                  fullWidth
                />
                <TenantDateTimeField
                  label={t('assignmentsAdmin.newDialog.lockAt')}
                  value={lockAt}
                  onChange={setLockAt}
                  fullWidth
                  helperText={t('assignmentsAdmin.newDialog.lockAtHelp')}
                />
              </Stack>
              {!isSocratic && (
                <Stack direction="row" spacing={2}>
                  <TextField
                    size="small"
                    label={t('assignmentsAdmin.newDialog.weight')}
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(Number(e.target.value))}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label={t('assignmentsAdmin.newDialog.points')}
                    type="number"
                    value={points}
                    onChange={(e) => setPoints(Number(e.target.value))}
                    sx={{ flex: 1 }}
                  />
                </Stack>
              )}
            </Stack>
          )}
          {!isQuiz && !isSocratic && (
            <FormControl size="small" error={missingChannel}>
              <InputLabel>{t('assignmentsAdmin.newDialog.submissionTypes')}</InputLabel>
              <Select
                multiple
                label={t('assignmentsAdmin.newDialog.submissionTypes')}
                value={submissionTypes}
                onChange={(e) => setSubmissionTypes(e.target.value as SubmissionType[])}
              >
                <MenuItem value="file">
                  {t('assignmentsAdmin.newDialog.submissionType.file')}
                </MenuItem>
                <MenuItem value="text">
                  {t('assignmentsAdmin.newDialog.submissionType.text')}
                </MenuItem>
                <MenuItem value="url">
                  {t('assignmentsAdmin.newDialog.submissionType.url')}
                </MenuItem>
              </Select>
              {missingChannel && (
                <FormHelperText>
                  {t('assignmentsAdmin.newDialog.submissionTypesRequired')}
                </FormHelperText>
              )}
            </FormControl>
          )}
          {!isQuiz && !isSocratic && (
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isSignature}
                    onChange={(e) => setIsSignature(e.target.checked)}
                  />
                }
                label={tCap('signature.toggle')}
              />
              {isSignature && (
                <FormControl size="small" fullWidth>
                  <InputLabel>{tCap('signature.ploSelect')}</InputLabel>
                  <Select
                    label={tCap('signature.ploSelect')}
                    value={
                      ploOptions.some((p) => p.id === evaluatesOutcomeId) ? evaluatesOutcomeId : ''
                    }
                    onChange={(e) => setEvaluatesOutcomeId(e.target.value)}
                  >
                    {ploOptions.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {ploOptions.length === 0 ? tCap('signature.noPlos') : tCap('signature.ploHelp')}
                  </FormHelperText>
                </FormControl>
              )}
            </Stack>
          )}
          {isQuiz && (
            <Alert severity="info">{t('assignmentsAdmin.quiz.dedicatedPageNotice')}</Alert>
          )}
          {!isQuiz && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <input
                type="checkbox"
                checked={publish}
                onChange={(e) => setPublish(e.target.checked)}
              />
              <Typography variant="body2">{t('assignmentsAdmin.newDialog.publishNow')}</Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('assignmentsAdmin.newDialog.cancel')}
        </Button>
        {isQuiz ? (
          <Button variant="contained" onClick={goToQuizBuilder} disabled={submitting || !sectionId}>
            {t('assignmentsAdmin.quiz.continueToBuilder')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || missingChannel}
          >
            {submitting
              ? t('assignmentsAdmin.newDialog.creating')
              : t('assignmentsAdmin.newDialog.create')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Full edit of a non-quiz assignment (name, instructions, dates, weight, points,
 *  submission types). Quiz assignments edit their content on the dedicated builder. */
function EditAssignmentDialog({
  assignment,
  tenantTz,
  moduleOptions,
  placementsByAssignment,
  onClose,
  onSaved,
  onStructureChanged,
}: {
  assignment: AssignmentSummaryDto | null;
  tenantTz: string;
  /** When embedded in the cockpit, the section's modules so placement can be
   *  managed from the edit dialog. */
  moduleOptions?: { id: string; title: string }[];
  /** Current module placements per assignment id (moduleId + item id). */
  placementsByAssignment?: Record<string, { moduleId: string; itemId: string }[]>;
  onClose: () => void;
  onSaved: () => Promise<void>;
  /** Called after placements change so the cockpit's Módulos tab refreshes. */
  onStructureChanged?: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { t: tCap } = useTranslation('cap');
  const { currentTenantId } = useTenantStore();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [lockAt, setLockAt] = useState('');
  const [weight, setWeight] = useState(0);
  const [points, setPoints] = useState(100);
  const [submissionTypes, setSubmissionTypes] = useState<SubmissionType[]>(['file']);
  const [moduleIds, setModuleIds] = useState<string[]>([]);
  // CAP signature (PR4): saved via its own guarded callable, not the generic update.
  const [isSignature, setIsSignature] = useState(false);
  const [evaluatesOutcomeId, setEvaluatesOutcomeId] = useState('');
  const [ploOptions, setPloOptions] = useState<
    { id: string; label: string; capCode: string; order: number }[]
  >([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!assignment) return;
    setName(assignment.name);
    setDescription(instructionsToText(assignment.description));
    setDueAt(assignment.dueAt ? isoToLocalInput(assignment.dueAt, tenantTz) : '');
    setLockAt(assignment.lockAt ? isoToLocalInput(assignment.lockAt, tenantTz) : '');
    setWeight(assignment.weight);
    setPoints(assignment.pointsPossible);
    // Show what is actually stored. Defaulting to ['file'] here used to hide a
    // broken assignment: the form claimed "Archivo" while the student's submit
    // dialog rendered nothing.
    setSubmissionTypes(assignment.submissionTypes);
    setModuleIds((placementsByAssignment?.[assignment.id] ?? []).map((p) => p.moduleId));
    setIsSignature(assignment.isSignatureAssignment === true);
    setEvaluatesOutcomeId(assignment.evaluatesOutcomeId ?? '');
    setErr(null);
  }, [assignment, tenantTz, placementsByAssignment]);

  // PLOs of the section's program (section → programId → programOutcomes).
  // Same section→program hop NewAssignmentDialog uses for the rubric catalog.
  useEffect(() => {
    let cancelled = false;
    const loadPlos = async (): Promise<void> => {
      if (!assignment || !currentTenantId) return;
      try {
        const sectionSnap = await getDoc(
          doc(db, `tenants/${currentTenantId}/sections/${assignment.sectionId}`),
        );
        const programId = sectionSnap.data()?.['programId'] as string | undefined;
        if (!programId) {
          if (!cancelled) setPloOptions([]);
          return;
        }
        const options = await loadSignaturePlos(currentTenantId, programId);
        if (!cancelled) setPloOptions(options);
      } catch {
        if (!cancelled) setPloOptions([]);
      }
    };
    void loadPlos();
    return () => {
      cancelled = true;
    };
  }, [assignment, currentTenantId]);

  // An upload-based assignment with no channel is unsubmittable for the student.
  const missingChannel =
    !!assignment && requiresSubmissionChannel(assignment.type) && submissionTypes.length === 0;

  const handleSave = async () => {
    if (!currentTenantId || !assignment) return;
    if (!name.trim()) {
      setErr(t('assignmentsAdmin.newDialog.nameRequired'));
      return;
    }
    if (dueAt && lockAt && lockAt < dueAt) {
      setErr(t('assignmentsAdmin.editLockDate.beforeDue'));
      return;
    }
    if (missingChannel) {
      setErr(t('assignmentsAdmin.newDialog.submissionTypesRequired'));
      return;
    }
    if (isSignature && !evaluatesOutcomeId) {
      setErr(tCap('signature.ploRequired'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await assignmentApi.update({
        tenantId: currentTenantId,
        assignmentId: assignment.id,
        updates: {
          name: name.trim(),
          description: description.trim(),
          // Wall-clock as-is; the server converts it in the tenant's zone. Empty → null (unset).
          dueAt: dueAt || null,
          lockAt: lockAt || null,
          weight,
          pointsPossible: points,
          submissionTypes,
        },
      });
      // Sync module placements: add newly-checked modules, remove unchecked ones.
      if (moduleOptions) {
        const original = placementsByAssignment?.[assignment.id] ?? [];
        const origIds = new Set(original.map((p) => p.moduleId));
        const selected = new Set(moduleIds);
        let placementsChanged = false;
        for (const mid of moduleIds) {
          if (!origIds.has(mid)) {
            await lmsApi.createModuleItem({
              tenantId: currentTenantId,
              sectionId: assignment.sectionId,
              moduleId: mid,
              type: 'assignment',
              targetId: assignment.id,
            });
            placementsChanged = true;
          }
        }
        for (const p of original) {
          if (!selected.has(p.moduleId)) {
            await lmsApi.deleteModuleItem({ tenantId: currentTenantId, itemId: p.itemId });
            placementsChanged = true;
          }
        }
        if (placementsChanged && onStructureChanged) await onStructureChanged();
      }
      // Signature tag travels through its own guarded callable (integrity
      // check server-side) — only when the pair actually changed.
      const signatureChanged =
        isSignature !== (assignment.isSignatureAssignment === true) ||
        (isSignature && evaluatesOutcomeId !== (assignment.evaluatesOutcomeId ?? ''));
      if (signatureChanged) {
        await capApi.tagSignatureAssignment({
          tenantId: currentTenantId,
          assignmentId: assignment.id,
          isSignature,
          ...(isSignature ? { evaluatesOutcomeId } : {}),
        });
      }
      await onSaved();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!assignment} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('assignmentsAdmin.editDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Alert severity="error">{err}</Alert>}
          {assignment && (
            <Chip
              size="small"
              label={t(`assignmentsAdmin.types.${assignment.type}`)}
              sx={{ alignSelf: 'flex-start' }}
            />
          )}
          <TextField
            size="small"
            label={t('assignmentsAdmin.newDialog.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label={t('assignmentsAdmin.newDialog.instructions')}
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TenantDateTimeField
                label={t('assignmentsAdmin.newDialog.dueAt')}
                value={dueAt}
                onChange={setDueAt}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TenantDateTimeField
                label={t('assignmentsAdmin.newDialog.lockAt')}
                value={lockAt}
                onChange={setLockAt}
                fullWidth
                helperText={t('assignmentsAdmin.newDialog.lockAtHelp')}
              />
            </Box>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              label={t('assignmentsAdmin.newDialog.weight')}
              type="number"
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value))}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label={t('assignmentsAdmin.newDialog.points')}
              type="number"
              value={points}
              onChange={(e) => setPoints(Number(e.target.value))}
              sx={{ flex: 1 }}
            />
          </Stack>
          <FormControl size="small" error={missingChannel}>
            <InputLabel>{t('assignmentsAdmin.newDialog.submissionTypes')}</InputLabel>
            <Select
              multiple
              label={t('assignmentsAdmin.newDialog.submissionTypes')}
              value={submissionTypes}
              onChange={(e) => setSubmissionTypes(e.target.value as SubmissionType[])}
            >
              <MenuItem value="file">
                {t('assignmentsAdmin.newDialog.submissionType.file')}
              </MenuItem>
              <MenuItem value="text">
                {t('assignmentsAdmin.newDialog.submissionType.text')}
              </MenuItem>
              <MenuItem value="url">{t('assignmentsAdmin.newDialog.submissionType.url')}</MenuItem>
            </Select>
            {missingChannel && (
              <FormHelperText>
                {t('assignmentsAdmin.newDialog.submissionTypesRequired')}
              </FormHelperText>
            )}
          </FormControl>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch checked={isSignature} onChange={(e) => setIsSignature(e.target.checked)} />
              }
              label={tCap('signature.toggle')}
            />
            {isSignature && (
              <FormControl size="small" fullWidth>
                <InputLabel>{tCap('signature.ploSelect')}</InputLabel>
                <Select
                  label={tCap('signature.ploSelect')}
                  value={
                    ploOptions.some((p) => p.id === evaluatesOutcomeId) ? evaluatesOutcomeId : ''
                  }
                  onChange={(e) => setEvaluatesOutcomeId(e.target.value)}
                >
                  {ploOptions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {ploOptions.length === 0 ? tCap('signature.noPlos') : tCap('signature.ploHelp')}
                </FormHelperText>
              </FormControl>
            )}
          </Stack>
          {moduleOptions && moduleOptions.length > 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel>
                {t('assignmentsAdmin.editDialog.modules', { defaultValue: 'Módulos' })}
              </InputLabel>
              <Select
                multiple
                label={t('assignmentsAdmin.editDialog.modules', { defaultValue: 'Módulos' })}
                value={moduleIds}
                onChange={(e) =>
                  setModuleIds(
                    typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                  )
                }
                renderValue={(selected) => (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {selected.map((id) => {
                      const m = moduleOptions.find((mm) => mm.id === id);
                      return <Chip key={id} size="small" label={m?.title ?? id} />;
                    })}
                  </Stack>
                )}
              >
                {moduleOptions.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {t('assignmentsAdmin.editDialog.modulesHelp', {
                  defaultValue: 'Módulos donde el alumno ve y completa esta tarea.',
                })}
              </FormHelperText>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('assignmentsAdmin.editDialog.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={submitting || missingChannel}>
          {submitting
            ? t('assignmentsAdmin.editDialog.saving')
            : t('assignmentsAdmin.editDialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
